import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { BaridClientService } from '../barid/barid-client.service';
import { BaridAgentStatus, BaridOfflineMode } from '../barid/barid-contracts';

export interface OfflineUiState {
  /** Chave OFFLINE_EDGE ligada E o Barid deste computador ja a adotou. `false` = comportamento de hoje. */
  enabled: boolean;
  mode: BaridOfflineMode;
  pending: number;
  held: number;
  modeSince?: string;
}

export const OFFLINE_STATE_DISABLED: OfflineUiState = Object.freeze({
  enabled: false,
  mode: 'ONLINE',
  pending: 0,
  held: 0
}) as OfflineUiState;

export const OFFLINE_POLL_ENABLED_MS = 5_000;
export const OFFLINE_POLL_DISABLED_MS = 30_000;

const MODES: readonly BaridOfflineMode[] = ['ONLINE', 'DEGRADED', 'OFFLINE', 'RESYNCING'];

/** Estado de conectividade offline lido do Barid local (`/status.offline`). Ausente/erro/OFF = desligado. */
export function offlineStateFromStatus(status: BaridAgentStatus | null | undefined): OfflineUiState {
  const offline = status?.offline;
  if (!status?.online || !offline || offline.enabled !== true) return OFFLINE_STATE_DISABLED;
  return {
    enabled: true,
    mode: MODES.includes(offline.mode) ? offline.mode : 'ONLINE',
    pending: Math.max(0, Number(offline.pending) || 0),
    held: Math.max(0, Number(offline.held) || 0),
    ...(offline.modeSince ? { modeSince: offline.modeSince } : {})
  };
}

/**
 * Fonte unica do estado offline para o indicador e para o `OfflineInterceptor`. O polling so comeca quando
 * alguem chama `start()` (o indicador da barra superior, que ja so existe para quem tem o Barid) e fica lento
 * (30 s) enquanto o offline esta desligado. Sem Barid ou com a chave OFF o estado e sempre `enabled:false`.
 */
@Injectable({ providedIn: 'root' })
export class OfflineStateService implements OnDestroy {
  private readonly subject = new BehaviorSubject<OfflineUiState>(OFFLINE_STATE_DISABLED);
  readonly state$ = this.subject.asObservable();
  private poll?: Subscription;

  constructor(private readonly agent: BaridClientService) {}

  get state(): OfflineUiState {
    return this.subject.value;
  }

  /** Offline ligado E o Barid em OFFLINE: so neste caso o interceptor desvia rotas da whitelist. */
  get routingActive(): boolean {
    const s = this.subject.value;
    return s.enabled && s.mode === 'OFFLINE';
  }

  start(): void {
    if (this.poll || !this.agent.enabled) return;
    this.schedule(0);
  }

  stop(): void {
    this.poll?.unsubscribe();
    this.poll = undefined;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Aplica um status ja lido por outro componente (evita uma segunda ida ao loopback). */
  ingest(status: BaridAgentStatus | null): void {
    this.subject.next(offlineStateFromStatus(status));
  }

  private schedule(delayMs: number): void {
    this.poll = timer(delayMs).subscribe(() => {
      this.agent.getStatus().subscribe({
        next: (status) => this.afterPoll(status),
        error: () => this.afterPoll(null)
      });
    });
  }

  private afterPoll(status: BaridAgentStatus | null): void {
    this.ingest(status);
    if (!this.poll) return; // parado durante a leitura
    this.poll.unsubscribe();
    this.schedule(this.subject.value.enabled ? OFFLINE_POLL_ENABLED_MS : OFFLINE_POLL_DISABLED_MS);
  }
}
